import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { brandApi, Brand, normalizeBrandLogoUrl } from "../../api/brandApi";

/**
 * Last-resort compiled fallback. This only runs when both the server-side
 * shell render (window.__BRAND__) and the /api/brand fetch fail. Kept
 * tenant-neutral on purpose — we never want to show one brand's label
 * ("AiVR Manager" was KPN's historical portal name) on another tenant's
 * portal, not even for a split second.
 *
 * portalName stays empty so the header renders only the logo; the tab
 * title uses the documentTitle fallback below so the browser never ends
 * up showing the raw URL.
 */
const FALLBACK_BRAND: Brand = {
  managerId: 0,
  portalName: "",
  primaryColor: "#00A0E3",
  logoUrl: null,
  documentTitle: "Portal",
};

type Status = "loading" | "ready" | "error";

interface BrandContextValue {
  brand: Brand;
  status: Status;
  /** Re-fetch the brand (e.g. after the admin saves a new colour). */
  refresh: () => Promise<void>;
}

const BrandContext = createContext<BrandContextValue>({
  brand: FALLBACK_BRAND,
  status: "loading",
  refresh: async () => {},
});

/**
 * Server-rendered bootstrap. SpaShellController inlines the resolved tenant
 * brand into the HTML shell as `window.__BRAND__` so React mounts with the
 * right colour + logo + title — no flash of another tenant's defaults.
 */
declare global {
  interface Window {
    __BRAND__?: Partial<Brand>;
  }
}

function readBootstrapBrand(): Brand {
  if (typeof window === "undefined") return FALLBACK_BRAND;
  const b = window.__BRAND__;
  if (!b) return FALLBACK_BRAND;
  return {
    managerId: typeof b.managerId === "number" ? b.managerId : FALLBACK_BRAND.managerId,
    // portalName may legitimately be empty — keep it verbatim so the
    // header stays blank when the tenant has no wordmark.
    portalName: typeof b.portalName === "string" ? b.portalName : FALLBACK_BRAND.portalName,
    primaryColor: b.primaryColor || FALLBACK_BRAND.primaryColor,
    logoUrl: normalizeBrandLogoUrl(b.logoUrl ?? null),
    documentTitle: b.documentTitle || FALLBACK_BRAND.documentTitle,
  };
}

/**
 * Keeps the brand in sync with the server after the initial render. The
 * first paint already uses the server-rendered values (via window.__BRAND__
 * + the inline <style> block that sets --portal-primary), so this effect is
 * only a validation pass that also picks up mid-session admin changes to
 * the colour/logo/name.
 */
export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<Brand>(readBootstrapBrand);
  const [status, setStatus] = useState<Status>(
    typeof window !== "undefined" && window.__BRAND__ ? "ready" : "loading",
  );

  const apply = useCallback((next: Brand) => {
    setBrand(next);
    if (typeof document !== "undefined") {
      // `!important` mirrors the server-rendered bootstrap <style>: that
      // inline block is marked !important to out-rank tokens.css / Vite's
      // dev CSS-in-JS, so an inline style property without the flag would
      // silently lose to it here. Keeping both sides in sync means the
      // admin's mid-session colour changes also win.
      document.documentElement.style.setProperty(
        "--portal-primary",
        next.primaryColor,
        "important",
      );
      // The tab title follows documentTitle (never empty) rather than
      // portalName (which may legitimately be blank when the tenant has
      // no wordmark). Guarded so an empty fallback can't wipe the title
      // to something worse than what the server rendered.
      if (next.documentTitle) {
        document.title = next.documentTitle;
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await brandApi.get();
      apply({
        managerId: next.managerId,
        portalName: typeof next.portalName === "string" ? next.portalName : FALLBACK_BRAND.portalName,
        primaryColor: next.primaryColor || FALLBACK_BRAND.primaryColor,
        logoUrl: normalizeBrandLogoUrl(next.logoUrl ?? null),
        documentTitle: next.documentTitle || FALLBACK_BRAND.documentTitle,
      });
      setStatus("ready");
    } catch {
      // The server-rendered bootstrap (or the compiled fallback) is still
      // in effect — the SPA must remain usable even when /api/brand is
      // unreachable. We only flag the status so a future UI could surface
      // a "could not refresh branding" toast.
      setStatus("error");
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Fix <link id="brand-favicon"> when HTML/bootstrap had a bad logo href (e.g. //api/…). */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const logo = normalizeBrandLogoUrl(brand.logoUrl);
    const link = document.querySelector<HTMLLinkElement>("#brand-favicon");
    if (!link) return;
    if (logo) {
      link.setAttribute("href", logo);
      link.setAttribute("type", "image/*");
    } else {
      link.setAttribute("href", "/favicon.svg");
      link.setAttribute("type", "image/svg+xml");
    }
  }, [brand.logoUrl]);

  const value = useMemo<BrandContextValue>(
    () => ({ brand, status, refresh }),
    [brand, status, refresh],
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
